/* eslint-disable no-console */
import { useApolloClient } from '@apollo/client';
import {
  Details,
  type FormActionData,
  type ObjectSchemaDefinition,
  ReadOnlyField,
  SingleLineTextField,
  type StationMessage,
  TextButton,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { Field, type FormikErrors, useFormikContext } from 'formik';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { config } from '../../../../config';
import {
  type GenerateServiceAccountTokenMutation,
  useGenerateServiceAccountTokenMutation,
} from '../../../../generated/id-service-auth-endpoint';
import { useServiceAccountQuery } from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './ServiceAccountGenerateToken.module.scss';

const accessTokenDefault = '*********************************';

const serviceAccountTokenSchema = Yup.object<ObjectSchemaDefinition>({
  clientSecret: Yup.string()
    .required('Client Secret is a required field')
    .max(100),
});

type GenerateServiceAccountTokenPayload =
  GenerateServiceAccountTokenMutation['authenticateServiceAccount'];

interface FormData {
  clientId: string;
  clientSecret: string;
}

let formData: FormData;
let validateFormData: () => Promise<FormikErrors<FormData>>;

export const ServiceAccountGenerateToken: React.FC = () => {
  const { envId, id } = useParams<{
    envId: string;
    id: string;
  }>();
  formData = { clientId: '', clientSecret: '' };

  const apolloClient = useApolloClient();

  // Query service account data
  const { loading, data, error } = useServiceAccountQuery({
    client: apolloClient,
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  const [serviceAccountAccessToken, setServiceAccountAccessToken] =
    useState<GenerateServiceAccountTokenPayload>({
      accessToken: accessTokenDefault,
    });

  const { actions, newServiceAccountAccessToken } = useGenerationActions(
    envId,
    serviceAccountAccessToken.accessToken !== accessTokenDefault,
  );

  useEffect(() => {
    if (newServiceAccountAccessToken) {
      setServiceAccountAccessToken(newServiceAccountAccessToken);
    }
  }, [newServiceAccountAccessToken]);

  const Form: React.FC<{
    serviceAccountToken: GenerateServiceAccountTokenPayload;
  }> = ({ serviceAccountToken }) => {
    const { setFieldValue } = useFormikContext<FormData>();

    const { values, validateForm } = useFormikContext<FormData>();
    validateFormData = () => {
      return validateForm(formData);
    };

    const onCopyClickedHandler = (): void => {
      try {
        // copy access-token to clipboard
        navigator.clipboard.writeText(serviceAccountToken?.accessToken ?? '');
      } catch (_error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to copy to clipboard');
      }
    };

    return (
      <>
        <Field name="clientId" label="Client ID" as={ReadOnlyField} />
        {serviceAccountAccessToken.accessToken === accessTokenDefault ? (
          <Field
            name="clientSecret"
            label="Client Secret"
            as={SingleLineTextField}
            type="password"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              formData = {
                clientId: values.clientId,
                clientSecret: e.target.value,
              };
              setFieldValue('clientSecret', e.target.value);
            }}
          />
        ) : null}
        <div
          className={clsx(classes.accessToken, {
            [classes.hasWarning]:
              serviceAccountToken?.accessToken !== accessTokenDefault,
          })}
        >
          <ReadOnlyField
            value={serviceAccountToken?.accessToken}
            label="Access Token"
          />
          {serviceAccountToken?.accessToken !== accessTokenDefault && (
            <>
              <TextButton
                className={classes.button}
                text="Copy"
                onButtonClicked={onCopyClickedHandler}
              />
            </>
          )}
        </div>
      </>
    );
  };

  const [message, setMessage] = useState<StationMessage>({
    type: 'info',
    title:
      'Enter the Client Secret in order to generate an access token using this Service Account. This will be valid for the duration configured in the Identity Service Configuration station.',
  });
  useEffect(() => {
    if (serviceAccountAccessToken?.accessToken !== accessTokenDefault) {
      setMessage({
        title: 'Please copy the access token displayed below.',
        type: 'info',
      });
    }
  }, [serviceAccountAccessToken?.accessToken]);

  return (
    <Details
      defaultTitle="Access Token"
      actions={actions}
      validationSchema={serviceAccountTokenSchema}
      initialData={{
        data: data?.serviceAccount,
        loading,
        error: error?.message,
      }}
      saveData={() => Promise.resolve()}
      stationMessage={message}
      showSaveHeaderAction={false}
    >
      <Form serviceAccountToken={serviceAccountAccessToken} />
    </Details>
  );
};

const useGenerationActions = (
  envId: string,
  accessTokenIsGenerated: boolean,
): {
  readonly actions: FormActionData[];
  readonly newServiceAccountAccessToken: GenerateServiceAccountTokenPayload;
} => {
  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.authBaseUrl).toString(),
  );

  const [generateServiceAccountToken] = useGenerateServiceAccountTokenMutation({
    client: envApolloClient,
    fetchPolicy: 'no-cache',
  });

  // Generate new access token using the service account
  const [serviceAccountAccessToken, setServiceAccountAccessToken] =
    useState<GenerateServiceAccountTokenPayload>({
      accessToken: accessTokenDefault,
    });

  const actions: FormActionData[] = [];
  if (!accessTokenIsGenerated) {
    actions.push({
      label: 'Generate Access Token',
      onActionSelected: async () => {
        const errors = await validateFormData();
        if (!errors.clientId && !errors.clientSecret) {
          try {
            const response = await generateServiceAccountToken({
              variables: {
                input: {
                  clientId: formData.clientId,
                  clientSecret: formData.clientSecret,
                },
              },
            });

            setServiceAccountAccessToken(
              response.data?.authenticateServiceAccount ?? {
                accessToken: accessTokenDefault,
              },
            );
          } catch (exception) {
            setServiceAccountAccessToken({
              accessToken: accessTokenDefault,
            });

            throw exception;
          }
        }
      },
    });
  }

  return {
    actions,
    newServiceAccountAccessToken: serviceAccountAccessToken,
  } as const;
};

export const ServiceAccountGenerateTokenCrumb: BreadcrumbResolver = () =>
  'Generate Token';
